import { useCallback, useEffect, useState } from 'react';
import { Download, FileText, Loader2, RefreshCw, Trash2 } from 'lucide-react';
import toast from 'react-hot-toast';
import AuthenticatedDownloadLink from '../AuthenticatedDownloadLink';
import {
  deleteGeneratedDocument,
  generatedDocumentDownloadUrl,
  listDocumentTemplates,
  listGeneratedDocuments,
  type DocumentTemplateInfo,
  type GeneratedDocument,
} from '../../lib/api';

function formatBytes(n: number): string {
  if (n < 1024) return `${n} B`;
  if (n < 1024 * 1024) return `${(n / 1024).toFixed(1)} KB`;
  return `${(n / (1024 * 1024)).toFixed(1)} MB`;
}

export default function DocumentsPanel() {
  const [docs, setDocs] = useState<GeneratedDocument[]>([]);
  const [templates, setTemplates] = useState<DocumentTemplateInfo[]>([]);
  const [configured, setConfigured] = useState(true);
  const [loading, setLoading] = useState(true);
  const [busyId, setBusyId] = useState<string | null>(null);

  const load = useCallback(async () => {
    setLoading(true);
    const [docsRes, tmplRes] = await Promise.all([
      listGeneratedDocuments({ limit: 30 }),
      listDocumentTemplates(),
    ]);
    setDocs(docsRes.ok && docsRes.data ? docsRes.data.items : []);
    if (tmplRes.ok && tmplRes.data) {
      setTemplates(tmplRes.data.templates);
      setConfigured(tmplRes.data.configured);
    }
    setLoading(false);
  }, []);

  useEffect(() => {
    void load();
  }, [load]);

  const handleDelete = async (id: string) => {
    setBusyId(id);
    const res = await deleteGeneratedDocument(id);
    setBusyId(null);
    if (res.ok) {
      toast.success('Document deleted');
      setDocs((prev) => prev.filter((d) => d.id !== id));
    }
  };

  return (
    <div className="flex h-full min-h-0 flex-col">
      <div className="flex shrink-0 items-center justify-between gap-2 border-b border-zt-border px-3 py-2">
        <div className="text-xs font-medium text-slate-200">Generated PDFs</div>
        <button
          type="button"
          onClick={() => void load()}
          className="rounded p-1 text-zt-muted hover:bg-zt-bg hover:text-slate-200"
          title="Refresh"
        >
          <RefreshCw size={13} className={loading ? 'animate-spin' : ''} />
        </button>
      </div>

      {!configured && (
        <div className="border-b border-zt-warning/30 bg-zt-warning/5 px-3 py-2 text-[11px] text-zt-warning">
          TEX_PDF_SERVICE_URL is not configured — generation is offline.
        </div>
      )}

      <div className="min-h-0 flex-1 overflow-y-auto p-3">
        {loading ? (
          <div className="flex items-center gap-2 text-xs text-zt-muted">
            <Loader2 size={13} className="animate-spin" /> Loading…
          </div>
        ) : docs.length === 0 ? (
          <div className="text-xs text-zt-muted">
            No generated documents yet. Ask the assistant to create an invoice, receipt, statement, or
            letter PDF.
          </div>
        ) : (
          <ul className="space-y-2">
            {docs.map((doc) => {
              const href = doc.downloadUrl || generatedDocumentDownloadUrl(doc.id);
              return (
                <li
                  key={doc.id}
                  className="rounded-lg border border-zt-border bg-zt-bg/40 px-2.5 py-2"
                >
                  <div className="flex items-start gap-2">
                    <FileText size={14} className="mt-0.5 shrink-0 text-zt-muted" />
                    <div className="min-w-0 flex-1">
                      <div className="truncate text-xs font-medium text-slate-200" title={doc.title}>
                        {doc.title || doc.filename}
                      </div>
                      <div className="mt-0.5 text-[10px] text-zt-muted">
                        {doc.documentType}
                        {doc.sizeBytes != null ? ` · ${formatBytes(doc.sizeBytes)}` : ''}
                        {doc.createdAt
                          ? ` · ${new Date(doc.createdAt).toLocaleString()}`
                          : ''}
                      </div>
                      <div className="mt-1.5 flex items-center gap-2">
                        <AuthenticatedDownloadLink
                          href={href}
                          forceAuthenticated
                          filenameHint={doc.filename || `${doc.title || doc.id}.pdf`}
                          className="inline-flex items-center gap-1 text-[11px] text-zt-accent hover:underline"
                        >
                          <Download size={11} /> Download
                        </AuthenticatedDownloadLink>
                        <button
                          type="button"
                          disabled={busyId === doc.id}
                          onClick={() => void handleDelete(doc.id)}
                          className="inline-flex items-center gap-1 text-[11px] text-zt-muted hover:text-zt-danger disabled:opacity-50"
                        >
                          <Trash2 size={11} /> Delete
                        </button>
                      </div>
                    </div>
                  </div>
                </li>
              );
            })}
          </ul>
        )}

        {templates.length > 0 && (
          <div className="mt-4 border-t border-zt-border pt-3">
            <div className="mb-1.5 text-[10px] uppercase tracking-wide text-zt-muted">Templates</div>
            <ul className="space-y-1">
              {templates.map((t) => (
                <li key={t.type} className="text-[11px] text-slate-300">
                  <span className="font-mono text-zt-muted">{t.type}</span>
                  <span className="text-zt-muted"> — </span>
                  {t.label}
                </li>
              ))}
            </ul>
          </div>
        )}
      </div>
    </div>
  );
}
